// App
import isNil from 'lodash/isNil';
import numeral from 'numeral';

// Constellation
import { Box, Button, ModalTrigger, Text } from '@zillow/constellation-10';

// Flux
import { useAppSelector } from '../flux/configureStore';

// Misc / utils
import { parser as comparablesParser } from '../utils/comparablesUtils';
import { pluralize } from '../utils/stringUtils';
import { compact, trimDecimal } from '../utils/numberUtils';
import HomeMarker from '../images/icons/HomeMarker';
import {
  cardAddressStyles,
  cardContainerRecipe,
  myPropertyBannerStyles,
  myPropertyFactsContainerStyles,
  myPropertyHeaderStyles,
} from './styles';

// Types
import type Comparables from '../models/Comparables';

type ZestimateType = { rentZestimate: number };
type MyPropertyCardPropTypes = {
  listing: Comparables & Partial<ZestimateType>;
  isHovered?: boolean;
  lockHover?: boolean;
};

const MAX_CHARS_PER_LINE = 21;

const MyPropertyCardComponent = ({ listing, isHovered, lockHover }: MyPropertyCardPropTypes) => {
  const longestAddress = useAppSelector((state) => comparablesParser.getLongestAddress(state.comparables.items));
  const priceFilter = useAppSelector((state) => state.filter.price);
  // prevent js error when setting custom price for addresses without a zestimate
  const myPropertyDisplayPrice =
    priceFilter || listing.rentZestimate ? compact(priceFilter || listing.rentZestimate) : null;

  // Calculate price per sqft safely - guard against zero, null, or invalid values
  const sqftValue = numeral(listing.sqft).value();
  const priceValue = numeral(myPropertyDisplayPrice).value();
  const hasSqft = sqftValue && sqftValue > 0;
  const hasPrice = priceValue && priceValue > 0;

  const myPropertyPriceSqft = hasSqft && hasPrice ? '$' + trimDecimal(priceValue / sqftValue, 2) : null;

  const listingPriceSqft = myPropertyPriceSqft ? `${myPropertyPriceSqft} per sq ft` : '—';

  const listingBeds = isNil(listing.beds) ? '—' : comparablesParser.beds(listing.beds);
  const listingBaths = isNil(listing.baths) ? '—' : pluralize('bath', listing.baths, true);
  const listingSqft = isNil(listing.sqft) ? '—' : `${listing.sqft} sq ft`;

  return (
    <Box css={cardContainerRecipe.raw({ isHovered: !!(isHovered || lockHover) })}>
      <Box css={myPropertyHeaderStyles}>
        <Text textStyle="body-sm-bold" css={myPropertyBannerStyles}>
          My property
        </Text>
        {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `height: 150px` falls between `3600` (144px) and `4000` (160px) on the numeric sizes scale */}
        <Box css={{ height: '150px', textAlign: 'center', marginTop: '400' }}>
          <HomeMarker height="80" width="100" style={{ background: 'transparent' }} />
        </Box>
        <Text textStyle="body-sm-bold">{`$${myPropertyDisplayPrice}/mo`}</Text>
        <Text textStyle="body-sm">{listingPriceSqft}</Text>
      </Box>
      <Box
        css={{
          ...cardAddressStyles,
          height: longestAddress.length > MAX_CHARS_PER_LINE ? '143px' : '105px',
        }}
      >
        {longestAddress.length > MAX_CHARS_PER_LINE ? (
          // oxlint-disable-next-line constellation/no-raw-spacing-values -- `marginTop: 27px` falls between `600` (24px) and `700` (28px) on the numeric spacing scale
          <Text textStyle="body-sm" css={{ marginBottom: '200', marginTop: '27px' }}>
            {listing.street}
          </Text>
        ) : (
          <Text textStyle="body-sm" css={{ marginBlock: '200' }}>
            {listing.street}
          </Text>
        )}
        <ModalTrigger multiModalTriggerId="change-price-modal">
          <Button size="sm" tone="brand" emphasis="filled" css={{ marginBottom: '200' }}>
            Adjust price
          </Button>
        </ModalTrigger>
      </Box>
      <Box css={myPropertyFactsContainerStyles}>
        <Text textStyle="body-sm-bold" css={{ marginBlock: '200' }}>
          {listingBeds}
        </Text>
        <Text textStyle="body-sm-bold" css={{ marginBottom: '200' }}>
          {listingBaths}
        </Text>
        <Text textStyle="body-sm-bold" css={{ marginBottom: '200' }}>
          {listingSqft}
        </Text>
      </Box>
    </Box>
  );
};

const MyPropertyCard = (props: MyPropertyCardPropTypes) => {
  const { listing } = props;
  if (!listing) {
    return null;
  }
  return <MyPropertyCardComponent {...props} />;
};

export default MyPropertyCard;
